import React, { useState } from 'react';
import {
  Drawer, Descriptions, Tag, Space, Button, DatePicker, Typography, Divider, message, Popconfirm, Tooltip,
} from 'antd';
import {
  PhoneOutlined, WhatsAppOutlined, CheckOutlined, CloseOutlined, DeleteOutlined,
  CalendarOutlined, UserOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  completeFollowup, cancelFollowup, deleteFollowup, updateFollowup,
} from '../services/callService';
import { TYPE_COLORS, STATUS_COLORS, PRIORITY_COLORS } from './callConstants';

const { Text } = Typography;
const fmt = (d) => (d ? dayjs(d).format('DD MMM YYYY, hh:mm A') : '—');

// Reusable drawer to view a follow-up and run quick actions.
const FollowupDetailDrawer = ({ open, followup, onClose, onChanged }) => {
  const [reschedule, setReschedule] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!followup) return null;

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); message.success(okMsg); onChanged && onChanged(); onClose(); }
    catch { message.error('Action failed'); }
    finally { setBusy(false); }
  };

  const doReschedule = async () => {
    if (!reschedule) { message.warning('Pick a new date/time'); return; }
    await act(() => updateFollowup(followup.FollowupID, { ScheduledAt: reschedule.format('YYYY-MM-DD HH:mm:ss') }), 'Rescheduled');
  };

  const phone = (followup.CustomerMobile || '').replace(/[^0-9]/g, '');
  const isPending = followup.Status === 'Pending';

  return (
    <Drawer
      title={<Space><UserOutlined />{followup.CustomerName || 'Follow-up'}</Space>}
      open={open}
      onClose={onClose}
      width={520}
    >
      <Space wrap style={{ marginBottom: 16 }}>
        <Button icon={<PhoneOutlined />} href={`tel:${followup.CustomerMobile}`}>Call</Button>
        <Button icon={<WhatsAppOutlined style={{ color: '#25D366' }} />} href={`https://wa.me/${phone}`} target="_blank">WhatsApp</Button>
        {isPending && <Button type="primary" icon={<CheckOutlined />} loading={busy}
          onClick={() => act(() => completeFollowup(followup.FollowupID), 'Marked completed')}>Complete</Button>}
        {isPending && <Button icon={<CloseOutlined />} loading={busy}
          onClick={() => act(() => cancelFollowup(followup.FollowupID), 'Cancelled')}>Cancel</Button>}
        <Popconfirm title="Delete this follow-up?" okText="Delete" okButtonProps={{ danger: true }}
          onConfirm={() => act(() => deleteFollowup(followup.FollowupID), 'Deleted')}>
          <Button danger icon={<DeleteOutlined />}>Delete</Button>
        </Popconfirm>
      </Space>

      <Descriptions column={1} size="small" bordered labelStyle={{ width: 140 }}>
        <Descriptions.Item label="Status">
          <Tag color={STATUS_COLORS[followup.Status] || 'default'}>{followup.Status}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Type">
          {followup.FollowupType
            ? <Tag color={TYPE_COLORS[followup.FollowupType] || 'blue'}>{followup.FollowupType}</Tag> : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Customer">{followup.CustomerName || '—'}</Descriptions.Item>
        <Descriptions.Item label="Mobile">{followup.CustomerMobile || '—'}</Descriptions.Item>
        <Descriptions.Item label="City">{followup.City || '—'}</Descriptions.Item>
        <Descriptions.Item label="Priority">
          {followup.Priority ? <Tag color={PRIORITY_COLORS[followup.Priority] || 'default'}>{followup.Priority}</Tag> : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Executive">{followup.ExecutiveName || '—'}</Descriptions.Item>
        <Descriptions.Item label="Scheduled">{fmt(followup.ScheduledAt)}</Descriptions.Item>
        {followup.CallAt && <Descriptions.Item label="Call At">{fmt(followup.CallAt)}</Descriptions.Item>}
        {followup.CallStatus && <Descriptions.Item label="Call Result">{followup.CallStatus}{followup.NotConnectedReason ? ` — ${followup.NotConnectedReason}` : ''}</Descriptions.Item>}
        {followup.Outcome && <Descriptions.Item label="Outcome">{followup.Outcome}</Descriptions.Item>}
        {followup.LostReason && <Descriptions.Item label="Lost Reason">{followup.LostReason}</Descriptions.Item>}
        {followup.Duration ? <Descriptions.Item label="Duration">{followup.Duration} min</Descriptions.Item> : null}
        {followup.CallType && <Descriptions.Item label="Call Type">{followup.CallType}</Descriptions.Item>}
        <Descriptions.Item label="Notes">{followup.Notes || '—'}</Descriptions.Item>
      </Descriptions>

      {isPending && (
        <>
          <Divider orientation="left" plain><CalendarOutlined /> Reschedule</Divider>
          <Space>
            <DatePicker showTime value={reschedule} onChange={setReschedule}
              format="DD MMM YYYY, hh:mm A" placeholder="New date & time" />
            <Button type="primary" loading={busy} onClick={doReschedule}>Update</Button>
          </Space>
        </>
      )}
    </Drawer>
  );
};

export default FollowupDetailDrawer;
